import React, {
  MouseEventHandler,
  ReactElement,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ActionButton,
  Button,
  Spacing,
  SpeedDialAction,
  Stack,
  styled,
  useBreakpoints,
} from 'ui';
import { LayoutContext } from '@context/LayoutContext';

const DefaultButton = styled(Button)({
  height: '56px',
  minWidth: '56px',
});

export type ToolbarItem = {
  name: string;
  icon: ReactElement;
  onClick: MouseEventHandler;
  onRender?: (ref: HTMLElement | null) => void;
  showNameOnDesktop?: boolean;
  variant?: 'primary' | 'action' | 'secondary';
  dataTestId?: string;
};

interface PageToolbarPortalProps {
  items: ToolbarItem[];
}

export const PAGE_TOOLBAR_PORTAL_ID = 'header-page-toolbar';

export function PageToolbarPortal() {
  return <div id={PAGE_TOOLBAR_PORTAL_ID} />;
}

const StyledStack = styled(Stack)`
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;
export const PageHeaderToolbar = ({ items }: PageToolbarPortalProps) => {
  const [portalEl, setPortalEl] = useState<HTMLElement>();
  const { isMdUp } = useBreakpoints();
  const { isSpeedDialOpen } = useContext(LayoutContext);
  const refs = useRef<(HTMLElement | null)[]>([]);

  useLayoutEffect(() => {
    const foundPortal = document.getElementById(
      isMdUp ? PAGE_TOOLBAR_PORTAL_ID : 'Utilities-actions',
    );
    if (!foundPortal)
      throw new Error('Portal container for PageHeaderToolbar does not exist');
    setPortalEl(foundPortal);
  }, [isMdUp]);

  const children = useMemo(
    () =>
      isMdUp ? (
        <StyledStack>
          {items.map(
            (
              {
                name,
                icon,
                onClick,
                onRender,
                showNameOnDesktop,
                variant = 'action',
                dataTestId,
              },
              index,
            ) => {
              if (onRender) onRender(refs.current[index]);
              return variant === 'action' ? (
                <ActionButton
                  data-testid={dataTestId}
                  key={name}
                  label={showNameOnDesktop ? name : undefined}
                  onClick={onClick}
                  ref={(el) => {
                    refs.current[index] = el;
                  }}
                  startIcon={icon}
                />
              ) : (
                <DefaultButton
                  data-testid={dataTestId}
                  key={name}
                  label={showNameOnDesktop ? name : undefined}
                  onClick={onClick}
                  ref={(el) => {
                    refs.current[index] = el;
                  }}
                  startIcon={icon}
                  variant={variant === 'primary' ? 'contained' : 'outlined'}
                />
              );
            },
          )}
        </StyledStack>
      ) : (
        items.map(({ name, icon, onClick }) => (
          <SpeedDialAction
            icon={icon}
            key={name}
            onClick={onClick}
            open={isSpeedDialOpen}
            tooltipTitle={name}
          />
        ))
      ),
    [isMdUp, items, isSpeedDialOpen],
  );

  if (!portalEl) return undefined;

  return createPortal(children, portalEl);
};
